import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { GiTeacher } from "react-icons/gi";
import {
  FaRocket,
  FaUserFriends,
  FaShieldAlt,
  FaChartLine,
  FaTrophy,
} from "react-icons/fa";
import { motion } from "framer-motion";
import HeroSection from "./Hero";
import QuickContact from "./QuickContact.js";
import WhySSS from "./WhySSS.jsx";

import CourseHighlights from "./CourseHighlights.js";
import { Button, Chip } from "@mui/material";
import { ArrowRight, MapPin, PhoneCall } from "lucide-react";
import AiSmartClassroom from "./AISmartClassroom.js";
import JourneySection from "./TestSection.js";
import FAQSection from "./FAQs.jsx";
import FacultySection from "./OurFaculty.jsx";
import { Link } from "react-router-dom";
import CarouselSection from "./CarouselSection.jsx";
import FoundersNoteSection from "./FounderNote.jsx";
import HomeCarousel from "./HomeCarousel.js";
import CampusLife from "./CampusLife.jsx";

const galleryItems = [
  {
    id: 1,
    title: "Classroom",
    description:
      "Interactive classroom sessions with expert faculty at SSS Achievers, Ghazipur.",
    img: "../images/gallery/classroom.webp",
  },
  {
    id: 2,
    title: "Founder Meet",
    description:
      "Inspiring sessions with our founders, guiding students toward success.",
    img: "../images/gallery/founder-meet.jpg",
  },
  {
    id: 3,
    title: "Library",
    description:
      "Quiet study spaces with extensive resources for board exam preparation.",
    img: "../images/gallery/library.webp",
  },
  {
    id: 4,
    title: "Mock Test",
    description:
      "Rigorous mock tests to prepare students for TET/CTET and other exams.",
    img: "../images/gallery/mock-test.jpg",
  },
  {
    id: 5,
    title: "Unit Test",
    description:
      "Regular unit tests to track progress in foundation and competitive courses.",
    img: "../images/gallery/class-test.jpg",
  },
  {
    id: 6,
    title: "Student Group",
    description:
      "Collaborative study groups fostering teamwork for SSC and UPSI prep.",
    img: "../images/gallery/student-group.jpg",
  },
  {
    id: 7,
    title: "Movie Day",
    description: "Fun movie days to relax and bond for students and faculty.",
    img: "../images/gallery/movie-day.jpg",
  },
  {
    id: 8,
    title: "Parents Meeting",
    description: "Engaging parents in their child’s journey at SSS Achievers.",
    img: "../images/gallery/parents-meeting.jpg",
  },
];
const HomePage = () => {
  return (
    <>
      <Helmet>
        <title>Best Coaching Institute in Ghazipur | SSS Achievers | Class 8 to 12</title>
        <meta name="description" content="SSS Achievers is the #1 coaching center in Ghazipur, UP. Expert coaching for Class 8–12, CBSE board exam prep, 95% success rate & free demo class. Serving Ghazipur, Ballia, Azamgarh, Mau & nearby areas." />
        <link rel="canonical" href="https://www.sssachievers.com/" />
        <meta property="og:title" content="Best Coaching Institute in Ghazipur | SSS Achievers" />
        <meta property="og:description" content="SSS Achievers is the #1 coaching center in Ghazipur for Class 8–12. Expert faculty, CBSE board prep, 95% success rate. Free demo class available!" />
        <meta property="og:url" content="https://www.sssachievers.com/" />
      </Helmet>
      <div>
        <HeroSection />
        <HomeCarousel />
      </div>

      {/* Board Results 2026 */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-8">
            <span className="inline-flex items-center gap-2 bg-[#0097b2]/10 text-[#0097b2] text-sm font-semibold px-4 py-1 rounded-full mb-3">
              <FaTrophy className="h-4 w-4" /> CBSE | ICSE Result 2026
            </span>
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Our <span className="text-[#0097b2]">Board Exam Toppers</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Congratulations to our Class 10 and Class 12 achievers! Led by
              Sonam Kumari with 97% in Class 10 ICSE and Keshav Bansal with
              95.2% in Class 12 Commerce, SSS Achievers students have once again
              delivered outstanding CBSE and ICSE board results in Ghazipur.
            </p>
          </div>
          <a
            href="/images/sss-icse-result.jpg"
            target="_blank"
            rel="noopener noreferrer"
            className="block overflow-hidden rounded-xl shadow-lg"
          >
            <img
              src="/images/sss-icse-result.jpg"
              alt="SSS Achievers CBSE and ICSE board result 2026 toppers, Ghazipur: Sonam Kumari 97% Class 10 ICSE, Keshav Bansal 95.2% Class 12 Commerce"
              className="w-full h-auto"
              loading="lazy"
            />
          </a>
        </div>
      </section>
      {/* <UpcomingBatches /> */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto course_section_highlight px-2">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Comprehensive{" "}
              <span className="text-[#0097b2]">Course Offerings</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Tailored programs designed to help students excel in various
              competitive examinations with personalized attention.
            </p>
          </div>
          <CourseHighlights />
          <div className="text-center mt-12 w-full flex justify-center">
            <Link to="/courses" className=" text-center">
              <button
                className="bg-[#0097b2] hover:bg-[#007a8f] text-white py-2 px-6 flex items-center rounded"
              >
                View All Courses <ArrowRight className="ml-2 h-4 w-4" />
              </button>
            </Link>
          </div>
        </div>
      </section>
      {/* <SSSTagline /> */}
      <AiSmartClassroom />
      <JourneySection />
      {/* <CarouselSection /> */}
      <div className=" py-8 px-4 sm:py-12">
        <div className="max-w-7xl mx-auto">
          {/* Gallery Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-1">
            {galleryItems.map((item) => (
              <div
                key={item.id}
                className="relative overflow-hidden group transition-all duration-300 hover:shadow-lg"
                style={{ margin: "2px" }} // 4px total margin (2px per side)
              >
                <div className="relative">
                  <img
                    src={item.img || "/placeholder.svg"}
                    alt={item.title}
                    className="w-full h-36 sm:h-44 md:h-52 object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <div className="p-2 sm:p-3 bg-white">
                  <h3 className="text-xs sm:text-sm md:text-base font-bold text-gray-900 mb-1 truncate">
                    {item.title}
                  </h3>
                  <p className="text-[10px] sm:text-xs md:text-sm text-gray-600 leading-relaxed line-clamp-2">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <FacultySection />
      {/* <CampusLife /> */}
      {/* <AIPoweredClasses /> */}
      <WhySSS />
      <FoundersNoteSection />
      <FAQSection />
      <div>{/* <CarosuelExample /> */}</div>
    </>
  );
};

export default HomePage;
